import { useEffect, useMemo } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { TabsProps } from 'src/components/primitives/Tabs/types';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

type Params<T extends string, S extends string> = Pick<
	TabsProps<T, S>,
	'options' | 'onTabChange' | 'activeTab'
>;

export default function useTabOptionsForScreenSize<
	T extends string,
	S extends string,
>({ options, onTabChange, activeTab }: Params<T, S>) {
	const isMobileScreenSize = useIsMobileScreenSize();

	const tapOptionsForScreenSize = useMemo(() => {
		return options.filter(({ screenSize }) => {
			return (
				!screenSize ||
				screenSize === 'all' ||
				(screenSize === 'mobile' && isMobileScreenSize) ||
				(screenSize === 'desktop' && !isMobileScreenSize)
			);
		});
	}, [options, isMobileScreenSize]);

	const activeTabExistsForScreenSize = useMemo(() => {
		return tapOptionsForScreenSize.some(({ value }) => value === activeTab);
	}, [tapOptionsForScreenSize, activeTab]);

	// Switch to the first tab if the active tab doesn't exist for this screen size.
	const onTabChangeRef = useSyncedRef(onTabChange);
	useEffect(() => {
		if (!activeTabExistsForScreenSize) {
			onTabChangeRef.current(tapOptionsForScreenSize[0].value as T);
		}
	}, [activeTabExistsForScreenSize, tapOptionsForScreenSize, onTabChangeRef]);

	return tapOptionsForScreenSize;
}
